import React, { useState } from "react";
import styles from "./CoinsCheck.module.css";

const transactionSections = [
  {
    key: "Videos",
    title: "Videos",
  },
  {
    key: "Ebooks",
    title: "eBooks",
  },
  {
    key: "Typings",
    title: "Typing",
  },
  {
    key: "TestTransactions",
    title: "Test transactions",
  },
];

function CoinsCheck() {
  const [email, setEmail] = useState("");
  const [coins, setCoins] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setCoins(null);
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(
        `https://onlineexam.ssccglpinnacle.com/api/total-coins?email=${encodeURIComponent(email.trim())}`,
      );

      if (!response.ok) {
        throw new Error("User not found or invalid email");
      }

      const data = await response.json();
      setCoins(data);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const scannedBarcodes = Array.isArray(coins?.scanned_barcodes)
    ? coins.scanned_barcodes
    : [];
  const transactionCount = transactionSections.reduce(
    (total, section) =>
      total +
      (Array.isArray(coins?.[section.key]) ? coins[section.key].length : 0),
    0,
  );
  const resultEmail =
    typeof coins?.email_id === "string" ? coins.email_id : email;

  return (
    <main className={styles.coinsCheckWrapper}>
      <div className={styles.coinsCheckPage}>
        <header className={styles.coinsCheckPageHeader}>
          <div>
            <span className={styles.coinsCheckEyebrow}>REWARDS &amp; LOYALTY</span>
            <h1 className={styles.coinsCheckTitle}>Student coins dashboard</h1>
            <p className={styles.coinsCheckSubtitle}>
              Review a student’s coin balance, barcode scans, and purchase activity.
            </p>
          </div>
          <span className={styles.coinsCheckHeaderBadge}>
            <span className={styles.coinsCheckHeaderDot} aria-hidden="true" />
            Account lookup
          </span>
        </header>

        <form
          onSubmit={handleSubmit}
          className={styles.coinsCheckLookup}
          aria-busy={loading}
        >
          <div className={styles.coinsCheckLookupCopy}>
            <span className={styles.coinsCheckSectionEyebrow}>STUDENT RECORD</span>
            <h2>Find an account</h2>
            <p>Use the email address registered to the student account.</p>
          </div>

          <div className={styles.coinsCheckControls}>
            <div className={styles.coinsCheckField}>
              <label htmlFor="coins-student-email">Student email</label>
              <div className={styles.coinsCheckInputWrap}>
                <span aria-hidden="true">@</span>
                <input
                  id="coins-student-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="student@example.com"
                  autoComplete="email"
                  required
                  className={styles.coinsCheckInput}
                />
              </div>
            </div>

            <button
              type="submit"
              className={styles.coinsCheckButton}
              disabled={loading}
            >
              {loading ? "Searching…" : "Search student"}
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </form>

        {loading && (
          <div className={styles.coinsCheckLoading} role="status">
            <span className={styles.coinsCheckSpinner} aria-hidden="true" />
            Looking up student account…
          </div>
        )}

        {error && (
          <div className={styles.coinsCheckError} role="alert">
            <span className={styles.coinsCheckErrorIcon} aria-hidden="true">
              !
            </span>
            <div>
              <strong>We couldn’t find that account</strong>
              <p>{error}</p>
            </div>
          </div>
        )}

        {coins && (
          <div className={styles.coinsCheckResults}>
            <section
              className={styles.coinsCheckProfile}
              aria-label="Student coin balance"
            >
              <div className={styles.coinsCheckProfileIdentity}>
                <span className={styles.coinsCheckAvatar} aria-hidden="true">
                  {(resultEmail || "S").charAt(0).toUpperCase()}
                </span>
                <div>
                  <span className={styles.coinsCheckSectionEyebrow}>
                    STUDENT ACCOUNT
                  </span>
                  <h2>Coin activity</h2>
                  <p>{resultEmail}</p>
                </div>
              </div>

              <div className={styles.coinsCheckBalance}>
                <span>Total coin balance</span>
                <div>
                  <strong>{coins.totalCoins ?? "—"}</strong>
                  <span>coins</span>
                </div>
              </div>
            </section>

            <section
              className={styles.coinsCheckMetrics}
              aria-label="Activity summary"
            >
              <article className={styles.coinsCheckMetric}>
                <span className={styles.coinsCheckMetricIcon} aria-hidden="true">
                  #
                </span>
                <div>
                  <span>Scanned barcodes</span>
                  <strong>{scannedBarcodes.length.toLocaleString()}</strong>
                </div>
              </article>
              <article className={styles.coinsCheckMetric}>
                <span
                  className={styles.coinsCheckMetricIcon}
                  aria-hidden="true"
                >
                  ↗
                </span>
                <div>
                  <span>Purchase records</span>
                  <strong>{transactionCount.toLocaleString()}</strong>
                </div>
              </article>
            </section>

            <section
              className={styles.coinsCheckActivity}
              aria-label="Student reward activity"
            >
              <div className={styles.coinsCheckSectionHeader}>
                <div>
                  <span className={styles.coinsCheckSectionEyebrow}>
                    REWARD ACTIVITY
                  </span>
                  <h2>Scanned barcodes</h2>
                  <p>Coins earned through verified product scans.</p>
                </div>
                <span className={styles.coinsCheckCount}>
                  {scannedBarcodes.length.toLocaleString()} scans
                </span>
              </div>

              {scannedBarcodes.length > 0 ? (
                <div className={styles.coinsCheckTableWrap}>
                  <table className={styles.coinsCheckTable}>
                    <thead>
                      <tr>
                        <th scope="col">Barcode</th>
                        <th scope="col">Coins earned</th>
                        <th scope="col">Scan time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scannedBarcodes.map((entry, index) => (
                        <tr key={entry._id || index}>
                          <td data-label="Barcode">
                            <span className={styles.coinsCheckBarcode}>
                              {entry.barcode || "—"}
                            </span>
                          </td>
                          <td data-label="Coins earned">
                            <span className={styles.coinsCheckCoinTag}>
                              {entry.GotCoins ?? "—"} coins
                            </span>
                          </td>
                          <td data-label="Scan time">
                            {entry.scan_ts &&
                            !Number.isNaN(new Date(entry.scan_ts).getTime())
                              ? new Date(entry.scan_ts).toLocaleString()
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.coinsCheckEmpty}>
                  <span className={styles.coinsCheckEmptyIcon} aria-hidden="true">
                    ◌
                  </span>
                  <div>
                    <strong>No barcode scans yet</strong>
                    <p>Verified scans will appear here when they are recorded.</p>
                  </div>
                </div>
              )}
            </section>

            <div className={styles.coinsCheckTransactionGrid}>
              {transactionSections.map((section) => {
                const items = Array.isArray(coins[section.key])
                  ? coins[section.key]
                  : [];

                return (
                  <section
                    key={section.key}
                    className={styles.coinsCheckSection}
                    aria-label={section.title + " transactions"}
                  >
                    <div className={styles.coinsCheckSectionHeader}>
                      <div>
                        <span className={styles.coinsCheckSectionEyebrow}>
                          PURCHASE ACTIVITY
                        </span>
                        <h2>{section.title}</h2>
                      </div>
                      <span className={styles.coinsCheckCount}>
                        {items.length.toLocaleString()} records
                      </span>
                    </div>

                    {items.length > 0 ? (
                      <div className={styles.coinsCheckTableWrap}>
                        <table className={styles.coinsCheckTable}>
                          <thead>
                            <tr>
                              <th scope="col">Order ID</th>
                              <th scope="col">Amount</th>
                              <th scope="col">Redeemed</th>
                              <th scope="col">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, index) => (
                              <tr key={item.order_id || index}>
                                <td data-label="Order ID">
                                  <span className={styles.coinsCheckOrderId}>
                                    {item.order_id || "—"}
                                  </span>
                                </td>
                                <td data-label="Amount">
                                  {item.order_amount != null
                                    ? `₹${item.order_amount}`
                                    : "—"}
                                </td>
                                <td data-label="Redeemed">
                                  <span className={styles.coinsCheckCoinTag}>
                                    {item.redeem_coins ?? "—"} coins
                                  </span>
                                </td>
                                <td data-label="Status">
                                  <span className={styles.coinsCheckStatus}>
                                    {item.status || "—"}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className={styles.coinsCheckEmpty}>
                        <span
                          className={styles.coinsCheckEmptyIcon}
                          aria-hidden="true"
                        >
                          ◌
                        </span>
                        <div>
                          <strong>No records yet</strong>
                          <p>
                            {section.title} activity will appear here when
                            available.
                          </p>
                        </div>
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

export default CoinsCheck;
